import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { finalize } from 'rxjs/operators';

import { CheckoutItem, CheckoutService } from '../checkout.service';
import { ErrorHandlerService } from '../../../common/services/error-handler.service';
import { FileStorageService } from '../../../common/services/file-storage.service';
import { MessageHandlerService } from '../../../common/services/message-handler.service';
import { formatJalali } from '../../../common/utils/date';

@Component({
  selector: 'ngx-checkout-accept-modal',
  templateUrl: './checkout-accept-modal.component.html',
  styleUrls: ['./checkout-accept-modal.component.scss'],
})
export class CheckoutAcceptModalComponent implements OnInit {
  @Input() checkoutId!: string;
  @Input() checkout?: CheckoutItem;

  form!: FormGroup;
  loading = false;
  detailLoading = false;
  detail?: CheckoutItem;

  constructor(
    protected ref: NbDialogRef<CheckoutAcceptModalComponent>,
    private fb: FormBuilder,
    private checkoutService: CheckoutService,
    private errorHandler: ErrorHandlerService,
    private fileStorageService: FileStorageService,
    private messageHandler: MessageHandlerService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      trackingCode: ['', Validators.required],
      receipt: [[], Validators.required],
      paymentDate: [null],
    });

    this.detail = this.checkout;
    this.checkoutId =
      this.checkoutId || this.checkout?.id || this.checkout?._id || '';

    if (!this.detail) {
      this.loadDetail();
    }
  }

  get trackingCodeControl() {
    return this.form.get('trackingCode');
  }

  get receiptControl() {
    return this.form.get('receipt');
  }

  get paymentDateControl() {
    return this.form.get('paymentDate');
  }

  get requesterName(): string {
    const entity = this.getRequester();
    if (!entity || typeof entity === 'string') return '-';

    return (
      entity?.fullName ||
      entity?.fullname ||
      [entity?.name || entity?.firstName, entity?.lastName]
        .filter(Boolean)
        .join(' ') ||
      entity?.mobile ||
      entity?.phone ||
      entity?.email ||
      '-'
    );
  }

  get requesterContact(): string {
    const entity = this.getRequester();
    if (!entity || typeof entity === 'string') return '';

    return entity?.mobile || entity?.phone || entity?.email || '';
  }

  get cardNumber(): string {
    return (
      this.detail?.cardNumber ||
      this.detail?.card?.cardNumber ||
      this.detail?.bankCard?.cardNumber ||
      '-'
    );
  }

  get sheba(): string {
    return (
      this.detail?.sheba ||
      this.detail?.card?.sheba ||
      this.detail?.bankCard?.sheba ||
      '-'
    );
  }

  get statusLabel(): string {
    const normalized = String(this.detail?.status || 'PENDING').toUpperCase();
    const labels: { [key: string]: string } = {
      PENDING: 'در انتظار بررسی',
      REQUESTED: 'در انتظار بررسی',
      ACCEPTED: 'تأیید شده',
      APPROVED: 'تأیید شده',
      PAID: 'پرداخت شده',
      COMPLETED: 'تکمیل شده',
      DONE: 'تکمیل شده',
      SUCCESS: 'موفق',
      SUCCESSFUL: 'موفق',
      REJECTED: 'رد شده',
      FAILED: 'ناموفق',
      CANCELLED: 'لغو شده',
      CANCELED: 'لغو شده',
    };

    return labels[normalized] || this.detail?.status || '-';
  }

  get createdAtLabel(): string {
    return this.detail?.createdAt
      ? formatJalali(this.detail.createdAt, true) || '-'
      : '-';
  }

  loadDetail(): void {
    if (!this.checkoutId) return;

    this.detailLoading = true;

    this.checkoutService
      .getCheckoutDetail(this.checkoutId)
      .pipe(finalize(() => (this.detailLoading = false)))
      .subscribe({
        next: (detail) => {
          this.detail = detail;
        },
        error: (error) => {
          this.errorHandler.handle(error);
        },
      });
  }

  async submit(): Promise<void> {
    if (this.loading) return;

    const receiptFile = this.getReceiptFile();

    if (!this.checkoutId || this.form.invalid || !receiptFile) {
      if (!receiptFile) {
        this.receiptControl?.setErrors({ required: true });
      }
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    this.loading = true;

    let receipt: string;
    try {
      receipt = await this.uploadReceipt(receiptFile);
    } catch (error) {
      this.loading = false;
      this.errorHandler.handle(error, this.form);
      return;
    }

    this.checkoutService
      .acceptCheckout({
        id: this.checkoutId,
        trackingCode: String(raw.trackingCode || '').trim(),
        receipt,
        paymentDate: this.parsePaymentDate(raw.paymentDate),
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.messageHandler.handle(
            'تایید درخواست',
            'درخواست با موفقیت تایید شد.',
          );

          this.ref.close(res || true);
        },
        error: (error) => {
          this.errorHandler.handle(error, this.form);
        },
      });
  }

  close(): void {
    this.ref.close();
  }

  private getReceiptFile(): File | null {
    const receipt = this.receiptControl?.value;
    const firstReceipt = Array.isArray(receipt) ? receipt[0] : receipt;

    return firstReceipt instanceof File ? firstReceipt : null;
  }

  private uploadReceipt(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalName', file.name);
    formData.append('isPublic', 'true');

    return new Promise((resolve, reject) => {
      this.fileStorageService.uploadFile(formData).subscribe({
        next: (res) => {
          const fileId =
            res?.data?.id ||
            res?.data?._id ||
            res?.data?.file?.id ||
            res?.data?.file?._id;

          fileId ? resolve(fileId) : reject(res);
        },
        error: (error) => reject(error),
      });
    });
  }

  private parsePaymentDate(value: string | null): Date | undefined {
    if (!value) return undefined;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  private getRequester(): any {
    return (
      this.detail?.user ||
      this.detail?.requester ||
      this.detail?.operator?.user ||
      this.detail?.operator ||
      this.detail?.agent?.user ||
      this.detail?.agent
    );
  }
}
